import { useMemo } from 'react';
import classnames from 'classnames';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { APP_LINKS } from '@/helpers/links';
import { prettyDate } from '@/helpers/date';
import { REPORTING_STATUS } from '@/helpers/reporting';
import { prettyFloatMoney } from '@/helpers/utils';
import { DATE_REQUEST } from '@/helpers/common';
import { MyLink } from '@/components/MyLink/index';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

export const ReportingCard = ({ report }) => {
  const { t } = useTranslation('reporting');
  const data = useMemo(() => {
    const estoniaDate = moment.tz(report.due_date, DATE_REQUEST, 'Europe/Tallinn');
    const now = moment();
    const diffInDays = estoniaDate.diff(now, 'days');

    switch (report.status_report) {
      case REPORTING_STATUS.AWAITING:
        if (diffInDays < 1)
          return {
            uploadRequired: true,
            status: t('reporting_card.awaiting'),
            text: t('reporting_card.submission_deadline_today'),
          };

        if (diffInDays < 5)
          return {
            uploadRequired: true,
            status: t('reporting_card.awaiting'),
            text: t('reporting_card.deadline_approaching', {
              date: prettyDate(report.due_date, 'MMMM DD'),
            }),
          };

        return {
          uploadRequired: true,
          status: t('reporting_card.awaiting'),
          text: t('reporting_card.upload_required', {
            date: prettyDate(report.due_date, 'MMMM DD'),
          }),
        };
      case REPORTING_STATUS.OVERDUE:
        if (report.penalty?.days_left === null) {
          return {
            uploadRequired: true,
            status: t('reporting_card.overdue'),
            text: t('reporting_card.non_compliance_fee_applied', {
              amount: prettyFloatMoney(report.penalty?.amount),
            }),
          };
        }

        return report.penalty?.days_left < 1
          ? {
              uploadRequired: true,
              status: t('reporting_card.overdue'),
              text: t('reporting_card.upload_today', {
                amount: prettyFloatMoney(report.penalty?.amount),
              }),
            }
          : {
              uploadRequired: true,
              status: t('reporting_card.overdue'),
              text: t('reporting_card.upload_within_days', {
                days: report.penalty?.days_left,
                amount: prettyFloatMoney(report.penalty?.amount),
              }),
            };
      case REPORTING_STATUS.UPLOADED_LATE:
        return {
          uploadRequired: false,
          status: t('reporting_card.uploaded'),
          text: t('reporting_card.uploaded_late', {
            amount: prettyFloatMoney(report?.penalty?.amount),
          }),
        };
      case REPORTING_STATUS.UPLOADED_ON_TIME:
        return {
          uploadRequired: false,
          status: t('reporting_card.uploaded'),
          text: t('reporting_card.thank_you_on_time'),
        };
      case REPORTING_STATUS.UPLOADED_ON_GRACE:
        return {
          uploadRequired: false,
          status: t('reporting_card.uploaded'),
          text: t('reporting_card.thank_you_grace_period'),
        };
      default:
        return {
          uploadRequired: false,
          status: '',
          text: '',
        };
    }
  }, [report]);

  return (
    <div className={s.card}>
      <div className={s.info}>
        <div className={s.info_block}>
          <div className="f-label">{t('reporting_card.period')}</div>
          <div className="f-body-regular">
            {prettyDate(report.period_from, 'MMM')} – {prettyDate(report.period_to, 'MMM YYYY')}
          </div>
        </div>
        <div className={s.info_block}>
          <div className="f-label">{t('reporting_card.due_date')}</div>
          <div className="f-body-regular">{prettyDate(report.due_date, 'MMMM DD, YYYY')}</div>
        </div>
        <div className={s.info_block}>
          <div className="f-label">{t('reporting_card.documents')}</div>
          <div className="f-body-regular">{t('reporting_card.document_list')}</div>
        </div>
      </div>
      {report.id ? (
        <div className={s.footer}>
          <div className={classnames(s.status, report.status_report.toLowerCase())}>
            {data.status}
          </div>
          <div className={classnames(s.text, 'f-body-regular')}>{data.text}</div>
          {data.uploadRequired && (
            <MyLink
              href={APP_LINKS.reportingUpload(report.id)}
              className={classnames(r.yellow_small, s.link)}
            >
              {t('reporting_card.upload_files')}
            </MyLink>
          )}
        </div>
      ) : (
        <div className={s.footer}>
          <div>
            {t('reporting_card.uploading_available', {
              date: prettyDate(report.due_date, 'MMMM DD, YYYY'),
            })}
          </div>
          <div className={classnames(r.grey_small, s.link)}>{t('reporting_card.upload_files')}</div>
        </div>
      )}
    </div>
  );
};
